// CreateUserAdminTab.jsx — Admin Panel "Create User" sub-tab, extracted from
// LimitedPartnerPortal. Invite form for new members. The newUserData draft
// state stays lifted in the portal because a parent effect pins the chapter
// field to the director's own chapter.
import React from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebaseConfig.js";
import { RetroButton } from "../../../ui/retro";
import { VALID_ROLES } from "../../../../data/roles";
import { roleLabel } from "../../../../helpers/labels";

const inviteUserCallable = httpsCallable(functions, "inviteUser");

export default function CreateUserAdminTab({
  isAdmin,
  newUserData,
  setNewUserData,
  isInviting,
  setIsInviting,
  isSuperAdmin,
  isChapterDirector,
  userChapter,
  activeChapterNames,
  loadAdminData,
  showAppAlert,
}) {
const handleCreateUser = async (e) => {
  e.preventDefault();

  if (!isAdmin) {
    showAppAlert("You do not have permission to invite users.");
    return;
  }
  if (isChapterDirector && newUserData.chapter && newUserData.chapter !== userChapter) {
    showAppAlert(`Chapter directors can only invite users to ${userChapter}.`);
    return;
  }
  if (newUserData.role === 'superAdmin' && !isSuperAdmin) {
    showAppAlert("Only Super Admins can create Super Admin accounts.");
    return;
  }
  if (!newUserData.email || !newUserData.name || !newUserData.chapter) {
    showAppAlert("Please fill in email, name, and chapter.");
    return;
  }

  setIsInviting(true);
  try {
    const result = await inviteUserCallable({
      email: newUserData.email.toLowerCase().trim(),
      name: newUserData.name.trim(),
      role: newUserData.role || 'lp',
      chapter: newUserData.chapter,
      anniversary: newUserData.anniversary,
      // Only meaningful for superAdmin invites — empty/undefined otherwise.
      chapterRole: newUserData.role === 'superAdmin' ? (newUserData.chapterRole || '') : '',
    });

    const data = result.data || {};
    const lines = [`Invite sent to ${data.email}.`];
    if (data.alreadyHadAuth) {
      lines.push(`Note: this email already had a Firebase Auth account, so we reused it.`);
    }
    if (data.emailSent === false) {
      lines.push(`The profile was created but the sign-in link email failed to send: ${data.emailError || 'unknown error'}. Use "Send link" in the users table to retry.`);
    } else {
      lines.push(`They'll receive a magic-link email — one tap and they're signed in. No password setup needed.`);
    }
    showAppAlert(lines.join('\n\n'));

    setNewUserData({
      email: '',
      name: '',
      role: 'lp',
      chapter: isChapterDirector ? userChapter : '',
      anniversary: new Date().toISOString().split('T')[0],
      chapterRole: '',
    });

    loadAdminData();
  } catch (error) {
    console.error('LPPortal: Error inviting user:', error);
    const msg = error?.message || 'Unknown error.';
    showAppAlert(`Failed to invite user: ${msg}`);
  } finally {
    setIsInviting(false);
  }
};

  return (
            <>
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    {isChapterDirector ? userChapter : "All chapters"} · magic-link invite
                  </div>
                  <h2 className="win95-pane-header__title">Create user</h2>
                </div>
              </div>

              <div className="admin-pane-body">
              <form onSubmit={handleCreateUser} style={{ maxWidth: '640px' }}>
                <fieldset className="win95-groupbox" style={{ marginBottom: 12 }}>
                  <legend>New member</legend>
                  <div className="admin-field-row">
                    <label htmlFor="new-user-email">
                      Email address <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                    </label>
                    <input
                      id="new-user-email"
                      type="email"
                      value={newUserData.email || ''}
                      onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value.toLowerCase() })}
                      required
                      placeholder="user@example.com"
                    />
                  </div>

                  <div className="admin-field-row">
                    <label htmlFor="new-user-name">
                      Full name <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                    </label>
                    <input
                      id="new-user-name"
                      type="text"
                      value={newUserData.name || ''}
                      onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                      required
                      placeholder="Jane Smith"
                    />
                  </div>

                  <div className="retro-form-grid">
                    <div className="admin-field-row" style={{ marginBottom: 0 }}>
                      <label htmlFor="new-user-role">
                        Role <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                      </label>
                      <select
                        id="new-user-role"
                        value={newUserData.role || 'lp'}
                        onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                        required
                      >
                        <option value="lp">Limited Partner (LP)</option>
                        <option value="chapter_director">Chapter director</option>
                        {isSuperAdmin && <option value="superAdmin">Super admin</option>}
                      </select>
                    </div>

                    <div className="admin-field-row" style={{ marginBottom: 0 }}>
                      <label htmlFor="new-user-chapter">
                        Chapter <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                      </label>
                      <select
                        id="new-user-chapter"
                        value={isChapterDirector ? (userChapter || '') : (newUserData.chapter || '')}
                        onChange={(e) => setNewUserData({ ...newUserData, chapter: e.target.value })}
                        required
                        disabled={isChapterDirector}
                        title={isChapterDirector ? `Chapter directors can only invite to ${userChapter}.` : undefined}
                      >
                        {isChapterDirector ? (
                          <option value={userChapter}>{userChapter}</option>
                        ) : (
                          <>
                            <option value="">— Select chapter —</option>
                            {activeChapterNames.map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* SuperAdmins can opt into appearing on their chapter's public
                       roster in a secondary role (LP or Chapter Director). The
                       role field still drives capabilities; chapterRole drives
                       presentation only. */}
                  {newUserData.role === 'superAdmin' && (
                    <div className="admin-field-row">
                      <label htmlFor="new-user-chapter-role">
                        List on chapter page as
                      </label>
                      <select
                        id="new-user-chapter-role"
                        value={newUserData.chapterRole || ''}
                        onChange={(e) => setNewUserData({ ...newUserData, chapterRole: e.target.value })}
                      >
                        <option value="">— Don't list —</option>
                        <option value="lp">Limited Partner (LP)</option>
                        <option value="chapter_director">Chapter director</option>
                      </select>
                    </div>
                  )}

                  <div className="admin-field-row">
                    <label htmlFor="new-user-anniversary">
                      Join date (anniversary) <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                    </label>
                    <input
                      id="new-user-anniversary"
                      type="date"
                      value={newUserData.anniversary || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setNewUserData({ ...newUserData, anniversary: e.target.value })}
                      required
                    />
                  </div>
                  </fieldset>

                  <div className="retro-action-row">
                    <RetroButton type="submit" variant="primary" disabled={isInviting}>
                      {isInviting ? 'Sending invite…' : 'Send invite'}
                    </RetroButton>
                    <RetroButton
                      type="button"
                      disabled={isInviting}
                      onClick={() => {
                        setNewUserData({
                          email: '',
                          name: '',
                          role: 'lp',
                          chapter: isChapterDirector ? userChapter : '',
                          anniversary: new Date().toISOString().split('T')[0],
                          chapterRole: '',
                        });
                      }}
                    >
                      Reset form
                    </RetroButton>
                  </div>
                </form>
              </div>
            </>
  );
}
